import { useContext } from '@nuxtjs/composition-api';
import { defineStore } from 'pinia';
import {
  computed,
  ref,
} from 'vue';

import { accountApiConfig } from '@/config/api/account';
import {
  changeCollectionTitle,
  loadCollectionById,
  loadCollections,
  loadFlatsForCollection,
} from '@/entities/collection/api';
import { useClientApi } from '@/plugins/api/lk';
import { useUserRole } from '@/shared/composables';
import { decorateApiActions } from '@/shared/lib';
import type {
  CollectionOrdering,
  CollectionRawLot,
  CollectionRequestParams,
  CurrentSentCollection,
  SentCollection,
  SentCollectionResponseData,
} from '@/store/collection/types';
import { COLLECTION_TYPES } from '@/store/collection/types';
import type { Lookup } from '@/types/Lookup';
import type {
  PageInfo,
  PageInfoGraphql,
} from '@/types/PageInfo';

const orderingCollection = [
  {
    label: 'Сначала новые',
    value: '-created_at',
  },
  {
    label: 'Сначала старые',
    value: 'created_at',
  },
];

export const useSentCollectionStore = defineStore('sentCollection', () => {
  const context = useContext();
  const pagedSentCollections = ref<Array<Array<SentCollection>>>([]);
  const sentCollections = computed(() => pagedSentCollections.value.flat(1));

  const loading = ref<boolean>(false);
  const ordering = ref<CollectionOrdering | null>(null);
  const search = ref<null | string>(null);
  const pageInfo = ref<null | PageInfo>(null);
  const lookup = ref<null | Lookup>(null);

  const flatsPageInfo = ref<null | PageInfo>(null);

  const currentSentCollection = ref<CurrentSentCollection | null>(null);
  const flatsInSentCollectionIds = ref<Array<string>>([]);
  const flatsInSentCollection = ref<Array<CollectionRawLot>>([]);
  const pageInfoFlats = ref<null | PageInfoGraphql>(null);

  async function getSpecs () {
    // пока так, будет пинать бэк чтобы делали спеки
    ordering.value = orderingCollection;
  }

  async function fetchLookup (params: CollectionRequestParams): Promise<Lookup> {
    const { clients } = accountApiConfig;
    const url = clients.lookup(useUserRole().role);
    lookup.value = await useClientApi().$get<Lookup>(url, { params });
    return lookup.value;
  }

  async function getSentCollections (params: CollectionRequestParams, append = false): Promise<SentCollectionResponseData> {
    const response = await loadCollections(params, COLLECTION_TYPES.sends);

    if (response.result.length && (params.page && params.page > 1)) {
      pagedSentCollections.value.push(response.result);
    } else {
      pagedSentCollections.value = [response.result];
    }

    pageInfo.value = response.page_info;
    return response;
  }

  async function getSentCollectionById (id: number) {
    try {
      loading.value = true;
      flatsInSentCollection.value = [];
      currentSentCollection.value = await loadCollectionById(id, COLLECTION_TYPES.sends);

      if (!currentSentCollection.value) {
        /* TODO: вывести ошибку */
        return;
      }

      flatsInSentCollectionIds.value = currentSentCollection.value.items.map((item) => item.profitbase_id.toString());

      if (!flatsInSentCollectionIds.value.length) {
        return;
      }

      const flatsResponse = await loadFlatsForCollection(flatsInSentCollectionIds.value);
      flatsInSentCollection.value = flatsResponse.edges.map((item: {
        node: CollectionRawLot;
      }) => item.node);
      pageInfoFlats.value = flatsResponse.pageInfo;
    } catch (error) {
      context.error({ statusCode: 404 });
    } finally {
      loading.value = false;
    }
  }

  async function nextPageFlats (): Promise<void> {
    try {
      if (!pageInfoFlats.value?.hasNextPage) {
        return;
      }

      const flatsResponse = await loadFlatsForCollection(flatsInSentCollectionIds.value, pageInfoFlats.value.endCursor);

      flatsInSentCollection.value = [
        ...flatsInSentCollection.value,
        ...flatsResponse.edges.map((item: {
          node: CollectionRawLot;
        }) => item.node),
      ];

      pageInfoFlats.value = flatsResponse.pageInfo;
    } catch (error: unknown) {
    }
  }

  async function changedSentCollectionTitle (id: number | string, title: string) {
    await changeCollectionTitle(Number(id), title, COLLECTION_TYPES.sends);
  }

  function dropLookupItems (): void {
    lookup.value = null;
  }

  function clear () {
    pagedSentCollections.value = [];
  }

  const decoratedApiActions = decorateApiActions({
    getSpecs,
    fetchLookup,
  }, 'collection/sentCollection.ts');

  const decoratedApiActionsRethrow = decorateApiActions({
    getSentCollectionsRethrow: getSentCollections,
  }, 'collection/sentCollection.ts', true);

  return {
    loading,
    ordering,
    search,
    pageInfo,
    flatsPageInfo,
    pagedSentCollections,
    sentCollections,
    currentSentCollection,
    flatsInSentCollection,
    lookup,

    getSentCollectionById,
    changedSentCollectionTitle,
    clear,
    dropLookupItems,
    ...decoratedApiActionsRethrow,
    ...decoratedApiActions,

    nextPageFlats,
    pageInfoFlats,

  };
});
